import { useEffect, useState } from 'react';
import { useLatest } from 'react-use';
import { featureCollection } from '@turf/turf';

import { emptyFeature, emptyGeojson } from '../../../utils/geojson';
import {
  FALLBACK_FEATURE_ID,
  MAIN_SOURCE_ID,
  LAND_BOUNDARIES_PREFIX as PREFIX,
} from '../constants';
import { getSelectedFeatures } from '../helpers';

const SELECTED_FEATURES_SOURCE_ID = `${PREFIX}-selected-features-source`;
const HOVERED_FEATURE_SOURCE_ID = `${PREFIX}-hovered-feature-source`;

const MAIN_LAYER_ID = `${PREFIX}-layer`;
const SELECTED_FEATURES_LAYER_ID = `${PREFIX}-selected-features-layer`;
const HOVERED_FEATURE_LAYER_ID = `${PREFIX}-hovered-feature-layer`;
const INTERACTION_LAYER_ID = `${PREFIX}-interaction-layer`;

const MAIN_COLOR = '#820000';
const HOVERED_COLOR = '#cccccc';
const SELECTED_COLOR = '#61a0ff';

// These are necessary to prevent some nasty mapbox bugs
// See https://github.com/mapbox/mapbox-gl-js/issues/7023
const SOURCE_SETTINGS = {
  // Setting tolerance to 0 reduces bugs related to coordinate conversions
  tolerance: 0,
  // Setting buffer to its max value and maxzoom to its min value reduces bugs related to tile cutoff when zooming
  buffer: 512,
  maxzoom: 12,
  // Setting promoteId to 'id' helps mapbox de-dup features with the same id
  promoteId: 'id',
};

const LandBoundariesLayer = ({
  mapRef,
  selectedFeatureIds,
  toggleFeatureId,
  isDrawMode,
}) => {
  // State for hovered feature:
  const [hoveredFeatureId, setHoveredFeatureId] = useState(null);

  // Ref for access without stale state within the useEffect
  const selectedFeatureIdsRef = useLatest(selectedFeatureIds);
  const isDrawModeRef = useLatest(isDrawMode);

  // Add the layers to the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return null;

    // main source
    map.addSource(MAIN_SOURCE_ID, emptyGeojson(SOURCE_SETTINGS));

    // source for just the selected features
    map.addSource(SELECTED_FEATURES_SOURCE_ID, emptyGeojson(SOURCE_SETTINGS));

    // source for just the hovered feature
    map.addSource(HOVERED_FEATURE_SOURCE_ID, emptyGeojson(SOURCE_SETTINGS));

    // main layer
    map.addLayer({
      id: MAIN_LAYER_ID,
      source: MAIN_SOURCE_ID,
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': MAIN_COLOR,
        'line-width': 2,
      },
    });

    // main layer
    map.addLayer({
      id: SELECTED_FEATURES_LAYER_ID,
      source: SELECTED_FEATURES_SOURCE_ID,
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': SELECTED_COLOR,
        'line-width': 2,
      },
      MAIN_LAYER_ID,
    });

    // layer for just the hovered feature
    map.addLayer({
      id: HOVERED_FEATURE_LAYER_ID,
      source: HOVERED_FEATURE_SOURCE_ID,
      type: 'fill',
      paint: {
        'fill-antialias': false,
        'fill-color': HOVERED_COLOR,
        'fill-opacity': 0.2,
      },
    });

    // layer for hover & click interactions
    map.addLayer({
      id: INTERACTION_LAYER_ID,
      source: MAIN_SOURCE_ID,
      type: 'fill',
      paint: {
        // Don't show the fill, this layer is just for hover/click events.
        'fill-opacity': 0,
      },
    });

    const onMouseMove = (e) => {
      const { id } = e.features[0].properties;
      // Don't highlight the feature if it's already selected
      if (id && !selectedFeatureIdsRef.current.includes(id)) {
        setHoveredFeatureId(id);
      } else {
        setHoveredFeatureId(null);
      }
    };

    const onMouseLeave = () => {
      setHoveredFeatureId(null);
    };

    const onClick = (e) => {
      // No interactions while drawing
      if (isDrawModeRef.current) return;

      const { id } = e.features[0].properties;

      // Don't let users deselect the fallback feature
      if (id === FALLBACK_FEATURE_ID) return;

      // clear the hover state temporarily so the user can see that they clicked on the feature
      setHoveredFeatureId(null);

      // toggle the feature's selected state
      toggleFeatureId(id);
    };

    map.on('mousemove', INTERACTION_LAYER_ID, onMouseMove);
    map.on('mouseleave', INTERACTION_LAYER_ID, onMouseLeave);
    map.on('click', INTERACTION_LAYER_ID, onClick);

    // cleanup
    return () => {
      if (!map || map._removed) return; // eslint-disable-line no-underscore-dangle

      map.off('mousemove', INTERACTION_LAYER_ID, onMouseMove);
      map.off('mouseleave', INTERACTION_LAYER_ID, onMouseLeave);
      map.off('click', INTERACTION_LAYER_ID, onClick);

      map.removeLayer(MAIN_LAYER_ID);
      map.removeLayer(SELECTED_FEATURES_LAYER_ID);
      map.removeLayer(HOVERED_FEATURE_LAYER_ID);
      map.removeLayer(INTERACTION_LAYER_ID);

      map.removeSource(MAIN_SOURCE_ID);
      map.removeSource(SELECTED_FEATURES_SOURCE_ID);
      map.removeSource(HOVERED_FEATURE_SOURCE_ID);
    };
  }, [
    mapRef, // (stable)
    selectedFeatureIdsRef, // (stable)
    isDrawModeRef, // (stable)
    toggleFeatureId, // (stable)
  ]);

  // When hoveredFeatureId changes, update the source for the hovered feature
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const data = emptyFeature();
    if (hoveredFeatureId) {
      const hoveredFeatures = map.querySourceFeatures(MAIN_SOURCE_ID, {
        filter: ['==', 'id', hoveredFeatureId],
      });
      if (hoveredFeatures) data.features = hoveredFeatures;
    }

    map.getSource(HOVERED_FEATURE_SOURCE_ID).setData(data);
  }, [hoveredFeatureId, mapRef]);

  // When selectedFeatureIds changes, update the source for the selected features
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const selectedFeatures = getSelectedFeatures(map, selectedFeatureIds);
    map
      .getSource(SELECTED_FEATURES_SOURCE_ID)
      .setData(featureCollection(selectedFeatures));
  }, [selectedFeatureIds, mapRef]);

  // When isDrawMode is true, hide some layers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const visibility = isDrawMode ? 'none' : 'visible';
    map.setLayoutProperty(HOVERED_FEATURE_LAYER_ID, 'visibility', visibility);
    map.setLayoutProperty(INTERACTION_LAYER_ID, 'visibility', visibility);
  }, [isDrawMode, mapRef]);

  return null;
};

export default LandBoundariesLayer;
